import { useViewCtx } from '../context/ViewContext'
import { speciesName, t, useLang, useSiteBrand } from '../i18n'
import { BASE_BRAND_ID } from '../brand/baseBrand'
import { useBrandName } from '../brand/pack'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { legendPlateLines, legendRows } from '../maps/legendRows'
import { driftPlateLines } from '../models/driftPlate'
import { scalePlateLine, scaleView, type ScaleInput } from '../maps/scaleRow'
import type { WaveSource } from '../maps/waveLabel'
import type { DrawnFlags } from '../maps/MapLegendPanel'
import type { ExportContext } from './exportPlate'
import { wrapPlateText } from './exportPlate'
import type { ToolsView } from './seriesOverride'

/**
 * Что писать на плашке скачанной картинки.
 *
 * Значения берутся из ОБЩЕГО контекста (дата · глубина · вид) — те же, что
 * показывает сайдбар: плашка обязана описывать ровно тот вид, который человек
 * видит, иначе картинка врёт о своей глубине.
 *
 * Легенда наложений строится тем же `legendRows`, что рисует плашку на карте:
 * экран и файл не могут разойтись, пока список один.
 *
 * `override` — значения кадра серии. Плашка обязана описывать снимаемый КАДР, а
 * не вид пользователя: иначе карта зоопланктона уехала бы в архив подписанной
 * как «Дрейфы, 30 м».
 */
export function useExportInfo(
  layerLabel: string,
  legend: {
    earthquakes: number
    waveSource?: WaveSource
    /** Нарисован ли в кадре снос: его легенда живёт отдельной карточкой на
     *  экране и иначе в файл не попадала (владелец 2026-08-08). */
    drift?: boolean
    /** Места вылова огрублены (признак кабинета): в кадре у них нет маркеров,
     *  только ореолы — и об этом на плашке должна быть строка. */
    driftAnonymized?: boolean
    /** Настройка кабинета: ореол погрешности скрыт. */
    driftCatchHaloHidden?: boolean
    /** Те же признаки пустоты, что у панели на экране: плашка обязана повторять
     *  её слово в слово, иначе файл спорит с картинкой. */
    sstData?: boolean
    sstPartial?: boolean
    waveData?: boolean
    surfaceData?: boolean
    drawn?: DrawnFlags
    /** Механизм апвеллинга, которым закрашено поле кадра; `undefined` — раздел
     *  не апвеллинговый. Смысловое пояснение обязано уехать и на картинку:
     *  иначе кадр апвеллинга уходит наружу ровно без того, ради чего пояснение
     *  и заведено (замечание пользователей 07.09.2026). */
    upwellingMechanism?: string
    /** Шкала растра кадра — та же, что панель на экране. Без неё плашка
     *  называет слой словами и молчит о том, что означает цвет; проверить такую
     *  картинку нечем (замечание пользователей 07.09.2026). `null` — слой
     *  шкалой не красится (виджет, геометрия). */
    scale?: ScaleInput | null
  } = { earthquakes: 0 },
  override: ToolsView | null = null,
): ExportContext {
  const ctx = useViewCtx()
  const { date, species, overlays } = ctx
  const depth = override?.depth ?? ctx.depth
  const depthApplicable = override ? override.depthApplicable : ctx.depthApplicable
  const isotherm = override ? override.isotherm : ctx.isotherm
  const isothermSurface = override ? override.isothermSurface : ctx.isothermSurface
  const isothermSpread = override ? override.isothermSpread : ctx.isothermSpread
  const fronts = override ? override.fronts : ctx.fronts
  const frontsUnit = override ? override.frontsUnit : ctx.frontsUnit
  const isobathStep = override ? override.isobathStep : ctx.isobathStep
  const waveOverlay = override ? override.waveOverlay : ctx.waveOverlay
  // Под серией плашка описывает оверлеи КАДРА: иначе кадр «Лёд» уехал бы в архив
  // подписанным рубежами и промрайонами, которые человек оставил включёнными.
  const shownOverlays = override ? override.overlays : overlays
  const { data } = useQuery({
    queryKey: ['cabinet', 'species'],
    queryFn: () => cabinetApi.species(),
    staleTime: Infinity,
  })
  // Подпись задаёт владелец в админке. Пока ответ не пришёл, `undefined` —
  // и плашка печатает зашитый адрес: молча снять подпись с картинки, которая
  // уходит наружу, из-за неотвеченного запроса нельзя.
  const { data: caption } = useQuery({
    queryKey: ['cabinet', 'export-caption'],
    queryFn: () => cabinetApi.exportCaption(),
    staleTime: 5 * 60_000,
  })
  // Бренд — из пака домена (тот же путь, что у знака в рельсе и имени в шапке).
  // Имя знает язык кабинета и само откатывается на каталог, когда пак не доехал;
  // код взять неоткуда — он не текст, поэтому запасной берётся константой.
  const brand = useSiteBrand()
  const brandName = useBrandName()
  const { lang } = useLang()
  const found = species != null ? data?.species?.find(s => s.species_id === species) : undefined
  const speciesLabel = found ? speciesName(found, lang) : null
  const scale = legend.scale ? scaleView(legend.scale) : null
  const rows = legendRows({
    isotherm,
    isothermSurface,
    isothermSpread,
    fronts,
    frontsUnit,
    isobathStep,
    waveOverlay,
    waveSource: legend.waveSource,
    sstData: legend.sstData,
    sstPartial: legend.sstPartial,
    waveData: legend.waveData,
    surfaceData: legend.surfaceData,
    isothermDrawn: legend.drawn?.isotherm,
    isothermSurfaceDrawn: legend.drawn?.isothermSurface,
    frontsDrawn: legend.drawn?.fronts,
    isobathsDrawn: legend.drawn?.isobaths,
    waveDrawn: legend.drawn?.waves,
    earthquakes: legend.earthquakes,
    // Рубежи и промысловые районы спрашиваются ровно так же, как на экране
    // (`MapLegendPanel`). До 07.09.2026 этих двух полей здесь не было, и линии
    // уезжали на скачанный кадр БЕЗ подписи — та самая безымянная краска, ради
    // которой единую легенду и собирали. Промах немой: `legendRows` берёт
    // состояние полями, забытое поле не падает, строка просто не появляется.
    // Теперь равенство двух списков держит сторож `exportLegendParity.test.ts`.
    eez: shownOverlays.includes('eez'),
    fishingDistricts: shownOverlays.includes('fishing_districts'),
    districtsDrawn: legend.drawn?.districts,
    bansDrawn: legend.drawn?.bans,
    bansDisclaimerKey: legend.drawn?.bansDisclaimerKey,
    bansEverywhere: legend.drawn?.bansEverywhere,
    substrate: legend.drawn?.substrate,
    navwarn: legend.drawn?.navwarn,
    nnMarks: legend.drawn?.nnMarks,
    moon: shownOverlays.includes('moon'),
    date,
  })
  return {
    brand: { id: brand?.id ?? BASE_BRAND_ID, name: brandName },
    layerLabel,
    date,
    depth,
    depthApplicable,
    speciesLabel,
    caption: caption?.text,
    // Снос идёт последним: наложения раскрашивают поле, а снос — отдельный
    // разговор поверх него, и на экране он тоже стоит отдельной карточкой.
    legend: [
      // Шкала — ПЕРВОЙ строкой легенды, сразу за названием слоя и датой: сперва
      // «что означает цвет», потом всё остальное.
      ...(scale ? [scalePlateLine(scale)] : []),
      // Смысл слоя — СРАЗУ за его названием: плашка сперва говорит, что на
      // картинке, потом что это значит, и только потом расшифровывает линии.
      // Текст связный, поэтому переносится по словам: плашка меряет ширину по
      // самой длинной строке, и два предложения одной строкой растянули бы её
      // через весь кадр.
      ...(legend.upwellingMechanism
        ? wrapPlateText(t(`upw.meaning.${legend.upwellingMechanism}`)).map(text => ({ text }))
        : []),
      ...legendPlateLines(rows),
      ...driftPlateLines(Boolean(legend.drift), {
        anonymized: Boolean(legend.driftAnonymized),
        catchHaloHidden: Boolean(legend.driftCatchHaloHidden),
      }),
    ],
  }
}
